import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { toast } from 'sonner';
import { 
  Star, 
  ChevronLeft,
  Loader2,
  ThumbsUp,
  Send
} from 'lucide-react';
import { doctorService } from '@/services/api';

const RateDoctorPage = () => {
  const { doctorId } = useParams();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  const [doctor, setDoctor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [review, setReview] = useState('');
  const [submitted, setSubmitted] = useState(false);
  
  const lang = i18n.language;
  const appointmentId = searchParams.get('appointment');

  useEffect(() => {
    const fetchDoctor = async () => {
      setLoading(true);
      try {
        const response = await doctorService.getById(doctorId);
        setDoctor(response.data.data || response.data);
      } catch (error) {
        console.error('Failed to fetch doctor:', error);
        // Mock doctor data
        setDoctor({
          id: doctorId,
          name: 'Dr. Sarah Johnson',
          name_ar: 'د. سارة جونسون',
          specialty: { name: 'Cardiology', name_ar: 'أمراض القلب' },
          image: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=200&h=200&fit=crop',
        });
      } finally {
        setLoading(false);
      }
    };
    fetchDoctor();
  }, [doctorId]);

  const handleSubmit = async () => {
    if (!isAuthenticated) {
      toast.error(lang === 'ar' ? 'يرجى تسجيل الدخول أولاً' : 'Please login first');
      navigate('/login');
      return;
    }

    if (rating === 0) {
      toast.error(lang === 'ar' ? 'يرجى اختيار تقييم' : 'Please select a rating');
      return;
    }

    setSubmitting(true);
    try {
      // Submit review to API
      await doctorService.addReview({
        doctor_id: doctorId,
        rate: rating,
        review: review,
        appointment_id: appointmentId,
      });
      
      setSubmitted(true);
      toast.success(
        lang === 'ar' ? 'شكراً لتقييمك!' : 'Thank you for your review!',
        { description: lang === 'ar' ? 'تقييمك يساعد الآخرين' : 'Your feedback helps others' }
      );
    } catch (error) {
      console.error('Failed to submit review:', error);
      toast.error(
        lang === 'ar' ? 'فشل في إرسال التقييم' : 'Failed to submit review',
        { description: error.response?.data?.message || 'Please try again' }
      );
    } finally {
      setSubmitting(false);
    }
  };

  const getRatingText = (r) => {
    const texts = {
      1: { en: 'Poor', ar: 'سيء' },
      2: { en: 'Fair', ar: 'مقبول' },
      3: { en: 'Good', ar: 'جيد' },
      4: { en: 'Very Good', ar: 'جيد جداً' },
      5: { en: 'Excellent', ar: 'ممتاز' },
    };
    return texts[r] ? (lang === 'ar' ? texts[r].ar : texts[r].en) : '';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="rate-success">
        <Card className="max-w-md w-full mx-4 text-center border-0 shadow-lg dark:bg-slate-800">
          <CardContent className="pt-12 pb-8">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <ThumbsUp className="h-10 w-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">
              {lang === 'ar' ? 'شكراً لك!' : 'Thank You!'}
            </h2>
            <p className="text-slate-600 mb-6">
              {lang === 'ar' 
                ? 'تقييمك يساعد الآخرين في اختيار الطبيب المناسب'
                : 'Your review helps others find the right doctor'
              }
            </p>
            <div className="flex gap-3 justify-center">
              <Button variant="outline" onClick={() => navigate('/appointments')}>
                {lang === 'ar' ? 'مواعيدي' : 'My Appointments'}
              </Button>
              <Button className="bg-teal-600 hover:bg-teal-700" onClick={() => navigate('/')}>
                {lang === 'ar' ? 'الرئيسية' : 'Home'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="rate-doctor-page">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 border-b dark:border-slate-700">
        <div className="container mx-auto px-4 py-6">
          <Button 
            variant="ghost" 
            size="sm" 
            className="mb-4 -ml-2"
            onClick={() => navigate(-1)}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            {t('common.back')}
          </Button>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            {lang === 'ar' ? 'تقييم الطبيب' : 'Rate Your Doctor'}
          </h1>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <Card className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
          <CardHeader className="text-center pb-2">
            <Avatar className="h-24 w-24 mx-auto mb-4 rounded-xl">
              <AvatarImage src={doctor?.image} alt={doctor?.name} className="object-cover" />
              <AvatarFallback className="rounded-xl bg-teal-600 text-white text-2xl">
                {doctor?.name?.charAt(0)}
              </AvatarFallback>
            </Avatar>
            <CardTitle>
              {lang === 'ar' && doctor?.name_ar ? doctor.name_ar : doctor?.name}
            </CardTitle>
            <CardDescription>
              {lang === 'ar' && doctor?.specialty?.name_ar 
                ? doctor.specialty.name_ar 
                : doctor?.specialty?.name
              }
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {/* Rating Stars */}
            <div className="text-center">
              <p className="text-sm text-slate-600 mb-3">
                {lang === 'ar' ? 'كيف كانت تجربتك؟' : 'How was your experience?'}
              </p>
              <div className="flex justify-center gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRating(star)}
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(0)}
                    className="p-1 transition-transform hover:scale-110 focus:outline-none"
                    data-testid={`star-${star}`}
                  >
                    <Star 
                      className={`h-10 w-10 transition-colors ${
                        star <= (hoverRating || rating) 
                          ? 'fill-amber-400 text-amber-400' 
                          : 'text-slate-300'
                      }`} 
                    />
                  </button>
                ))}
              </div>
              {(rating > 0 || hoverRating > 0) && (
                <p className="text-lg font-medium text-teal-600 mt-2">
                  {getRatingText(hoverRating || rating)}
                </p>
              )}
            </div>

            {/* Review Text */}
            <div>
              <p className="text-sm text-slate-600 mb-2">
                {lang === 'ar' ? 'شاركنا تجربتك (اختياري)' : 'Share your experience (optional)'}
              </p>
              <Textarea
                placeholder={lang === 'ar' 
                  ? 'اكتب تقييمك هنا... ما الذي أعجبك؟ ما الذي يمكن تحسينه؟'
                  : 'Write your review here... What did you like? What could be improved?'
                }
                value={review}
                onChange={(e) => setReview(e.target.value)}
                rows={4}
                className="resize-none"
                data-testid="review-input"
              />
            </div>

            {/* Submit Button */}
            <Button 
              className="w-full bg-teal-600 hover:bg-teal-700" 
              size="lg"
              onClick={handleSubmit}
              disabled={submitting || rating === 0}
              data-testid="submit-review-btn"
            >
              {submitting ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('common.loading')}</>
              ) : (
                <><Send className="h-4 w-4 mr-2" />{lang === 'ar' ? 'إرسال التقييم' : 'Submit Review'}</>
              )}
            </Button>

            <p className="text-xs text-center text-slate-400">
              {lang === 'ar' 
                ? 'تقييمك سيكون مرئياً للجميع ويساعد الآخرين في اختيارهم'
                : 'Your review will be public and help others make their choice'
              }
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RateDoctorPage;
